import { useSite } from "@/context/SiteContext";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Minus, Trash2, CheckCircle2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useCart, euro } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { createOrder } from "@/lib/apiData";

export default function Checkout() {
  const { items, subtotal, deliveryFee, serviceFee, total, minOrder, freeOver, updateQty, removeItem, clear } = useCart();
  const { config } = useSite();
  const belowMin = minOrder > 0 && subtotal < minOrder;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [placed, setPlaced] = useState(null);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    address: "",
    city: "",
    postcode: "",
    notes: "",
  });

  const placeOrder = async (e) => {
    e.preventDefault();
    const orderNo = `JL-${Math.floor(1000 + Math.random() * 9000)}`;
    const count = items.reduce((n, i) => n + i.qty, 0);
    try {
      await createOrder({
        order_no: orderNo,
        customer_name: form.name,
        customer_email: form.email,
        items: items.map((i) => ({ dish_id: i.id, name: i.name, price: i.price, qty: i.qty })),
        total,
      });
    } catch {
      /* non-blocking for demo */
    }
    setPlaced({ orderNo, total, count });
    clear();
    toast.success("Order placed successfully!");
    window.scrollTo(0, 0);
  };

  if (placed) {
    return (
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-24 text-center" data-testid="order-confirmed">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}>
          <CheckCircle2 className="w-16 h-16 text-[#d00000] mx-auto" />
          <h1 className="font-display text-4xl font-black text-neutral-900 mt-6">Order confirmed!</h1>
          <p className="text-neutral-600 mt-3">Thank you, {form.name || "friend"}. Your food is being prepared with love.</p>
          <div className="mt-8 border border-neutral-200 bg-white p-6 text-left inline-block">
            <p className="text-sm text-neutral-500">Order number</p>
            <p className="font-display text-2xl font-bold text-neutral-900" data-testid="order-number">{placed.orderNo}</p>
            <p className="text-sm text-neutral-500 mt-4">Total paid</p>
            <p className="font-display text-2xl font-bold text-[#d00000]">{euro(placed.total)}</p>
          </div>
          <div className="flex gap-3 justify-center mt-8">
            <Button onClick={() => navigate("/track")} className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none" data-testid="track-order-cta">Track order</Button>
            <Button onClick={() => navigate("/marketplace")} variant="ghost" className="border border-neutral-300 text-neutral-900 rounded-none">Keep browsing</Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-5 sm:px-8 py-24 text-center" data-testid="checkout-empty">
        <ShoppingBag className="w-14 h-14 text-neutral-300 mx-auto mb-4" />
        <h1 className="font-display text-3xl font-black text-neutral-900">Your cart is empty</h1>
        <p className="text-neutral-500 mt-2">Add some dishes before heading to checkout.</p>
        <Link to="/marketplace"><Button className="mt-6 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">Browse meals</Button></Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-3">Checkout</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-neutral-900">Almost there.</h1>

      <div className="grid lg:grid-cols-3 gap-10 mt-10">
        {/* Delivery form */}
        <form onSubmit={placeOrder} className="lg:col-span-2 space-y-5" data-testid="checkout-form">
          <h2 className="font-display text-2xl font-bold text-neutral-900">Delivery details</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Full name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name" className="mt-2 h-12 bg-white border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Email</Label>
              <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email" className="mt-2 h-12 bg-white border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Phone</Label>
              <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="checkout-phone" className="mt-2 h-12 bg-white border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Postcode</Label>
              <Input required value={form.postcode} onChange={(e) => setForm({ ...form, postcode: e.target.value })} data-testid="checkout-postcode" className="mt-2 h-12 bg-white border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
            </div>
          </div>
          <div>
            <Label className="text-neutral-500 text-xs uppercase tracking-widest">Street address</Label>
            <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="checkout-address" className="mt-2 h-12 bg-white border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
          </div>
          <div>
            <Label className="text-neutral-500 text-xs uppercase tracking-widest">City</Label>
            <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="checkout-city" className="mt-2 h-12 bg-white border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
          </div>
          <div>
            <Label className="text-neutral-500 text-xs uppercase tracking-widest">Delivery notes (optional)</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="checkout-notes" className="mt-2 bg-white border-neutral-300 rounded-none focus-visible:ring-[#d00000]" />
          </div>

          <div className="border border-neutral-200 bg-[#fbf9f7] p-4 text-sm text-neutral-600">
            Payment is collected on delivery for this demo. No card is charged.
            {config?.pricing?.service_info && <p className="mt-2 text-neutral-500" data-testid="checkout-service-info">{config.pricing.service_info}</p>}
          </div>
          {belowMin && <p className="text-sm text-[#d00000]" data-testid="checkout-min-order">Minimum order is {euro(minOrder)}. Add a little more to continue.</p>}

          <Button type="submit" disabled={belowMin} data-testid="place-order-btn" className="w-full h-14 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none text-base">
            Place order · {euro(total)}
          </Button>
        </form>

        {/* Order summary */}
        <div>
          <h2 className="font-display text-2xl font-bold text-neutral-900 mb-6">Order summary</h2>
          <div className="border border-neutral-200 bg-white">
            <div className="divide-y divide-neutral-100">
              {items.map((i) => (
                <div key={i.id} className="flex gap-3 p-4" data-testid={`summary-item-${i.id}`}>
                  <img src={i.img} alt={i.name} className="w-14 h-14 object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-neutral-900 text-sm font-medium leading-tight">{i.name}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button type="button" onClick={() => updateQty(i.id, i.qty - 1)} data-testid={`summary-dec-${i.id}`} className="w-6 h-6 border border-neutral-300 flex items-center justify-center hover:border-[#d00000]"><Minus className="w-3 h-3" /></button>
                      <span className="w-5 text-center text-sm">{i.qty}</span>
                      <button type="button" onClick={() => updateQty(i.id, i.qty + 1)} data-testid={`summary-inc-${i.id}`} className="w-6 h-6 border border-neutral-300 flex items-center justify-center hover:border-[#d00000]"><Plus className="w-3 h-3" /></button>
                      <button type="button" onClick={() => removeItem(i.id)} data-testid={`summary-remove-${i.id}`} className="ml-auto text-neutral-400 hover:text-[#d00000]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <span className="text-neutral-900 text-sm font-semibold">{euro(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-neutral-200 p-4 space-y-2">
              <div className="flex justify-between text-sm text-neutral-600"><span>Subtotal</span><span data-testid="checkout-subtotal">{euro(subtotal)}</span></div>
              <div className="flex justify-between text-sm text-neutral-600"><span>Delivery{freeOver > 0 && deliveryFee > 0 ? ` (free over ${euro(freeOver)})` : ""}</span><span data-testid="checkout-delivery">{deliveryFee ? euro(deliveryFee) : "Free"}</span></div>
              {serviceFee > 0 && <div className="flex justify-between text-sm text-neutral-600"><span>Service fee</span><span data-testid="checkout-service-fee">{euro(serviceFee)}</span></div>}
              <div className="flex justify-between text-lg font-bold text-neutral-900 pt-2 border-t border-neutral-100"><span>Total</span><span data-testid="checkout-total">{euro(total)}</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
